import { graphql } from 'app/generated/gql'
import { createGqlFetcher } from 'app/lib/gqlFetcher'
import { Disclosure } from '@headlessui/react'
import { ChevronDownIcon, XMarkIcon } from '@heroicons/react/20/solid'
import { withZod } from '@remix-validated-form/with-zod'
import classNames from 'classnames'
import { isEmpty } from 'lodash'
import moment from 'moment'
import { ReactElement, useMemo, useState } from 'react'
import { useFieldArray, useFormContext } from 'remix-validated-form'
import { z } from 'zod'
import Button from '../Button'
import {
  TypedAsyncSelectForm,
  TypedDateTimePicker,
  TypedFormInput,
  TypedPhoneFormInput,
  TypedSelectForm,
} from '../contacts/TypedFormField'
import { SmartForm, SubAction } from '../form/SmartForm'
import TagsCell from '../tags/TagsCell'
import { formatPhoneNumber } from 'app/formatPhoneNumber'
import type { CallJson } from 'app/types/entities'
import type { StaffOptionValue } from 'app/types/entities'
import type { LocationType } from 'app/types/entities'
import { notifyError, notifySuccess } from 'app/services/notify.client'
import { gqlMutation } from 'app/lib/graphqlClient'
import { logError } from 'lib/logging'
import { run } from 'lib/run'
import { OptionType } from 'types/OptionType'
import type { VariablesOf } from '@graphql-typed-document-node/core'

const UPDATE_CALL_MUTATION = graphql(`
  mutation CallInfoUpdateCall($id: ID!, $input: UpdateCallInput!) {
    updateCall(id: $id, input: $input) {
      version
      createdDate
      updatedDate
      id
      orgId
      workspaceId
      locationId
      elevenLabsConversationId
      archived
      channel
      vendor
      answerer
      answered
      ai {
        summary
        actionItems
        people {
          name
          treatment
          serviceRequested
          relationship
          appointmentDate
          appointmentTime
          phone
          patientType
          appointmentScheduled
          appointmentRequested
          birthDate
          referral
          insuranceProvider
          outcomes
          reasonForNotScheduling
          isCaller
          index
          email
        }
        answerer
        channel
        extractionDate
        model
        tokenUsage {
          inputTokens
          outputTokens
          totalTokens
        }
      }
      treatment
      patientNames
      callerId
      trackingNumber
      trackingNumberName
      tags
      waveforms
      appointmentDateString
      callerPhone
      businessPhone
      callDate
      note
      status
      recordingDuration
      duration
      utmSource
      utmMedium
      utmCampaign
      utmKeywords
      utmContent
      referringUrl
      gclid
      landingPageUrl
      recordings
      patients {
        name
        treatment
        serviceRequested
        relationship
        appointmentDate
        appointmentTime
        phone
        patientType
        appointmentScheduled
        appointmentRequested
        birthDate
        referral
        insuranceProvider
        outcomes
        reasonForNotScheduling
        isCaller
        index
        email
      }
      staff {
        id
        name
        aliases
        userId
      }
      aiAgent
      golden
      direction
      people {
        name
        treatment
        serviceRequested
        relationship
        appointmentDate
        appointmentTime
        phone
        patientType
        appointmentScheduled
        appointmentRequested
        birthDate
        referral
        insuranceProvider
        outcomes
        reasonForNotScheduling
        isCaller
        index
        email
      }
      allPatientOutcomes
    }
  }
`)

type PatientForm = {
  name: string
  phone: string
  locationName: string
  locationId: string
  outcome: CallOutcome | undefined
  procedure?: string
  appointmentDate: string | undefined
}

type MarketingChannel = `Digital Marketing` | `Traditional Marketing` | `Referral Marketing`
type CallOutcome =
  | `First Appointment Scheduled`
  | `Unscheduled Call`
  | `State Insurance`
  | `Out of Range`
  | `Canceled`
  | `Missed Call`
  | `Rescheduled Appointment`
  | `Voicemail`
  | `Answering Service: First Appointment Scheduled`
  | `Answering Service: Unscheduled Call`

const CALL_OUTCOME_OPTIONS: CallOutcome[] = [
  `First Appointment Scheduled`,
  `Unscheduled Call`,
  `State Insurance`,
  `Out of Range`,
  `Canceled`,
  `Missed Call`,
  `Rescheduled Appointment`,
  `Voicemail`,
  `Answering Service: First Appointment Scheduled`,
  `Answering Service: Unscheduled Call`,
]

const toMarketingChannel = (value?: string): MarketingChannel | undefined => {
  switch (value) {
    case `Digital Marketing`:
    case `Traditional Marketing`:
    case `Referral Marketing`:
      return value
    default:
      return undefined
  }
}

const toCallOutcome = (value?: string): CallOutcome | undefined => {
  switch (value) {
    case `First Appointment Scheduled`:
    case `Unscheduled Call`:
    case `State Insurance`:
    case `Out of Range`:
    case `Canceled`:
    case `Missed Call`:
    case `Rescheduled Appointment`:
    case `Voicemail`:
    case `Answering Service: First Appointment Scheduled`:
    case `Answering Service: Unscheduled Call`:
      return value
    default:
      return undefined
  }
}

const patientSchema = {
  name: z.string().min(1, { message: `Patient Name is required` }),
  phone: z.string().min(10, { message: `A valid phone number is required` }),
  locationId: z.object({
    id: z.string().min(1, { message: `Location ID is required` }),
    name: z.string().min(1, { message: `Location name is required` }),
    value: z.string().min(1, { message: `Location value is required` }),
  }),
  outcome: z
    .string()
    .min(1, { message: `Outcome is required` })
    .transform((value: string): CallOutcome | undefined => toCallOutcome(value)),
  procedure: z.string().optional(),
  appointmentDate: z
    .string()
    .optional()
    // An untouched datetime-local input submits an empty string. The field is
    // optional, so treat blank as "not given" rather than as an invalid date.
    .refine((date) => !date || moment.utc(date).isValid(), { message: `A valid date is required` })
    .transform((date) => date || undefined),
}

const callFormSchema = z.object({
  id: z.string().min(1, { message: `ID is required` }),
  patients: z
    .array(z.object(patientSchema).transform((patient) => ({ ...patient, locationId: patient.locationId.id })))
    .optional(),
  channel: z.string().optional().transform(toMarketingChannel),
  staff: z
    .object({ id: z.string(), name: z.string() })
    .partial()
    .optional()
    .transform((staff?: { id?: string; name?: string; value?: string }): StaffOptionValue | undefined => {
      if (isEmpty(staff) || !staff?.name) return undefined
      return { id: staff.id, name: staff.name }
    }),
})

type CallFormValues = z.infer<typeof callFormSchema>

export const callValidator = withZod(callFormSchema)

type CallPersonValue = NonNullable<CallJson[`patients`]>[number]

const isCallPersonValue = (patient: PatientForm | CallPersonValue): patient is CallPersonValue => {
  return `outcomes` in patient
}

const toPatientForm = (patient: PatientForm | CallPersonValue, locationId: string): PatientForm => {
  const outcome = isCallPersonValue(patient) ? toCallOutcome(patient.outcomes?.[0] ?? undefined) : patient.outcome
  // The API calls this `treatment`, with `serviceRequested` as what the AI
  // extraction filled in. The rest of the app reads the pair the same way.
  const procedure = isCallPersonValue(patient)
    ? patient.treatment || patient.serviceRequested || undefined
    : patient.procedure
  return {
    name: patient.name ?? ``,
    phone: formatPhoneNumber(patient.phone) ?? ``,
    locationName: `locationName` in patient ? patient.locationName : ``,
    locationId,
    outcome,
    procedure,
    appointmentDate: patient.appointmentDate ?? undefined,
  }
}

export function CallInfo({
  call,
  location,
  onCallUpdate,
}: {
  call: CallJson
  location?: LocationType
  onCallUpdate: (call: CallJson) => void
}): ReactElement {
  const [isDirtyForm, setIsDirtyForm] = useState<boolean>(false)
  const { setFieldTouched } = useFormContext(`call-drawer-form`)
  const [patients, { push, remove }] = useFieldArray<PatientForm>(`patients`, {
    formId: `call-drawer-form`,
  })
  let selectedLocation = useMemo(() => {
    if (location) return location
  }, [location])

  let emptyPatientDefaultValue: PatientForm = {
    name: ``,
    phone: formatPhoneNumber(call?.callerPhone) ?? ``,
    locationName: selectedLocation?.name!,
    locationId: selectedLocation?.id!,
    outcome: undefined,
    appointmentDate: undefined,
  }

  let defaultValues = useMemo<Partial<CallFormValues>>(
    () => ({
      id: call.id,
      staff: call.staff ? { id: call.staff.id, name: call.staff.name } : undefined,
      patients: (call.patients?.length ? call.patients : [emptyPatientDefaultValue]).map((patient) =>
        toPatientForm(patient, selectedLocation?.id ?? ``),
      ),
      channel: toMarketingChannel(call.channel ?? undefined),
    }),
    [location, call],
  )

  let getArrayProps = (index) => ({
    arrayFieldName: `patients`,
    arrayIndex: index,
    isArray: true,
  })

  let handleSubmit = (values?: CallFormValues) => {
    const callId = call.id
    if (!values || !callId) return
    // The form speaks the drawer's language; CallPatientInput takes a list of
    // outcomes and calls the requested work `treatment`.
    const patients = values.patients?.map(({ outcome, procedure, ...patient }) => ({
      ...patient,
      ...(outcome && { outcomes: [outcome] }),
      ...(procedure && { treatment: procedure }),
    }))
    // The id is the mutation's own argument. UpdateCallInput takes patients,
    // channel, staff, status, note, tags and ai, and rejects anything else.
    const { id: _id, ...rest } = values
    //If validation passes then status is Complete, no need to check
    const variables: VariablesOf<typeof UPDATE_CALL_MUTATION> = {
      id: callId,
      input: { ...rest, patients, status: `Complete` },
    }
    run(async () =>
      gqlMutation(UPDATE_CALL_MUTATION, variables)
        .then(async (result) => {
          if (result.data?.updateCall) {
            notifySuccess(`Call updated successfully`)
            // The drawer's callback speaks CallJson; the selection is the full call.
            onCallUpdate(result.data.updateCall as CallJson)
            return result.data
          }
        })
        .catch((err) => {
          notifyError(`Call update failed`)
          logError(err)
        }),
    ).catch((err) => {
      notifyError(`Call update failed`)
      logError(err)
    })
  }

  return (
    <div className="flex flex-col gap-y-4 overflow-hidden pt-4">
      <h5 className="px-4 capitalize">{call.callerId?.toLocaleLowerCase()}</h5>
      <div className="flex grow flex-col overflow-hidden">
        <SmartForm<CallFormValues>
          // The drawer stays mounted as it moves between calls. Without this the
          // form keeps the previous call's rows and edits.
          key={call.id}
          id="call-drawer-form"
          className="!mt-0 !flex w-full flex-col justify-start !gap-y-4 overflow-hidden bg-surface"
          subaction={SubAction.EDIT}
          validator={callValidator}
          defaultValues={defaultValues}
          onTouchedFields={(val) => setIsDirtyForm(val)}
          hiddenInputs={[{ name: `id`, value: call.id }]}
          onSubmit={handleSubmit}
          preventSubmit
          addDefaultClass
          hideButtons
        >
          <div className="flex grow flex-col gap-4 overflow-y-auto px-4">
            <div className="flex flex-col gap-y-3 divide-y divide-default">
              <TypedAsyncSelectForm<CallFormValues>
                label="Staff"
                field="staff"
                fetcher={createGqlFetcher('users')}
                locationId={call.locationId!}
                workspaceId={call.workspaceId!}
              />
              <TypedSelectForm<CallFormValues>
                field="channel"
                label="Channel"
                options={[`Digital Marketing`, `Traditional Marketing`, `Referral Marketing`].map<OptionType>(
                  (option) => ({ id: option, name: option, value: option }),
                )}
              />
            </div>
            {patients?.map((patient, index) => (
              <Disclosure
                as="div"
                key={index}
                className="rounded-lg border border-strong shadow-elevation-1"
                defaultOpen
              >
                {({ open }) => (
                  <>
                    <Disclosure.Button
                      as="div"
                      className={classNames(
                        `flex w-full cursor-pointer items-center justify-between bg-surface-subtle px-[18px] py-4`,
                        open ? `rounded-t-lg border-b border-strong` : `rounded-lg`,
                      )}
                    >
                      <span className="text-heading-xs text-strong">Patient {index + 1}</span>
                      <ChevronDownIcon className={`${open ? `rotate-180 transform` : ``} h-4 w-4 text-strong`} />
                    </Disclosure.Button>
                    <Disclosure.Panel>
                      <div className="flex flex-col gap-y-3 divide-y divide-default px-4 pb-4">
                        <TypedFormInput<PatientForm>
                          label="Patient's Name"
                          field="name"
                          {...getArrayProps(index)}
                          required
                        />
                        <TypedPhoneFormInput<PatientForm>
                          label="Phone Number"
                          field="phone"
                          {...getArrayProps(index)}
                          required
                        />
                        <TypedFormInput<PatientForm> label="Procedure" field="procedure" {...getArrayProps(index)} />
                        <TypedSelectForm<PatientForm>
                          label="Call outcome"
                          field="outcome"
                          options={CALL_OUTCOME_OPTIONS.map((option) => ({ id: option, name: option, value: option }))}
                          {...getArrayProps(index)}
                          required
                        />
                        <TypedDateTimePicker<PatientForm>
                          label="Apt Date"
                          field="appointmentDate"
                          {...getArrayProps(index)}
                          required
                          showErrorIcon
                        />
                        <TypedAsyncSelectForm<PatientForm>
                          label="Location"
                          field="locationId"
                          locationId={call.locationId!}
                          workspaceId={call.workspaceId!}
                          fetcher={createGqlFetcher('locations')}
                          default={
                            selectedLocation?.id && selectedLocation.name
                              ? { id: selectedLocation.id, name: selectedLocation.name, value: selectedLocation.name }
                              : undefined
                          }
                          {...getArrayProps(index)}
                          required
                        />
                        <div
                          className="flex cursor-pointer justify-center gap-x-2 pt-4 align-middle text-red-600"
                          onClick={() => {
                            remove(index)
                            if (call.patients && call.patients?.length) setFieldTouched(`patients`, true)
                          }}
                        >
                          <XMarkIcon className="h-4 w-4" />
                          <span className="text-ui-xs">Remove Patient</span>
                        </div>
                      </div>
                    </Disclosure.Panel>
                  </>
                )}
              </Disclosure>
            ))}
            <div className="flex max-w-[340px] flex-col justify-between gap-y-2 border border-strong p-3 align-middle text-strong">
              <span className=" text-ui-sm text-strong">Tags</span>
              <TagsCell
                model="calls"
                modelId={call.id!}
                workspaceId={call.workspaceId}
                tags={call.tags}
                shouldWrap={true}
                isOrgTags
              />
            </div>
          </div>
          <div className="sticky bottom-0 w-full border-t border-default bg-surface py-4">
            <div className="flex justify-center gap-x-4 px-4 align-middle">
              <Button
                type="button"
                label="Add Patient"
                mode="white"
                className="w-36 justify-center rounded-full px-6 py-2 text-ui-sm text-strong shadow-elevation-1"
                onClick={() => push(emptyPatientDefaultValue)}
              />
              <Button
                type="submit"
                label="Save"
                disabled={!isDirtyForm}
                mode="primary"
                className="w-36 justify-center rounded-full px-6 py-2 text-ui-sm text-on-color shadow-elevation-1 disabled:cursor-not-allowed"
              />
            </div>
          </div>
        </SmartForm>
      </div>
    </div>
  )
}
